import axios from 'axios';
import { all, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { MOBILE_ROUTES } from 'app/api-routes';
import { SORT_DIRECTION } from 'app/types/constants';

import {
  routesRequest,
  routesSuccess,
  routesFailure,
  detailRequest,
  detailSuccess,
  detailFailure,
  createRouteRequest,
  createRouteSuccess,
  createRouteFailure,
  updateRouteRequest,
  updateRouteSuccess,
  updateRouteFailure,
  copyRouteRequest,
  copyRouteSuccess,
  copyRouteFailure,
  suspendRouteRequest,
  suspendRouteSuccess,
  suspendRouteFailure,
  resumeRouteRequest,
  resumeRouteSuccess,
  resumeRouteFailure,
  deleteRouteRequest,
  deleteRouteSuccess,
  deleteRouteFailure,
  runsRequest,
  runsSuccess,
  runsFailure,
  runDetailRequest,
  runDetailSuccess,
  runDetailFailure,
  runsListRequest,
  runsListSuccess,
  runsListFailure,
  runByIdRequest,
  runByIdSuccess,
  runByIdFailure,
} from './actions';

// Server responds with `{ errors, payload, warnings }` and may include either
// `errorCode` (preferred, new style) or `errors[0].code` (legacy). Preserve
// both shapes plus the auxiliary payload so feature stories can render inline
// errors or modal prompts (e.g. ROUTE_RUN_ALREADY_IN_PROGRESS forwards
// `payload.inProgressRun`).
const failurePayloadFrom = (err) => {
  const data = err?.response?.data;
  const errorCode = data?.errorCode || data?.errors?.[0]?.code || null;
  return {
    errorCode,
    payload: data?.payload ?? null,
    error: err,
  };
};

function* routesSaga({ payload }) {
  try {
    const { payload: data } = yield axios.get(MOBILE_ROUTES, {
      params: payload,
    });
    yield put(routesSuccess({ ...payload, ...data }));
  } catch (err) {
    yield put(routesFailure(failurePayloadFrom(err)));
  }
}

function* detailSaga({ payload: mobileRouteID }) {
  try {
    const { payload } = yield axios.get(`${MOBILE_ROUTES}/${mobileRouteID}`);
    yield put(detailSuccess(payload));
  } catch (err) {
    yield put(detailFailure(failurePayloadFrom(err)));
  }
}

function* createRouteSaga({ payload, meta }) {
  try {
    const { payload: data } = yield axios.post(MOBILE_ROUTES, { payload });
    yield put(createRouteSuccess(data));
    meta?.onSuccess?.(data);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(createRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* updateRouteSaga({ payload: { mobileRouteID, payload }, meta }) {
  try {
    const { payload: data } = yield axios.put(`${MOBILE_ROUTES}/${mobileRouteID}`, {
      payload,
    });
    yield put(updateRouteSuccess(data));
    meta?.onSuccess?.(data);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(updateRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* copyRouteSaga({ payload: mobileRouteID, meta }) {
  try {
    const { payload } = yield axios.post(`${MOBILE_ROUTES}/${mobileRouteID}/copy`);
    yield put(copyRouteSuccess(payload));
    meta?.onSuccess?.(payload);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(copyRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* suspendRouteSaga({ payload: mobileRouteID, meta }) {
  try {
    yield axios.post(`${MOBILE_ROUTES}/${mobileRouteID}/suspend`);
    yield put(suspendRouteSuccess({ mobileRouteID }));
    meta?.onSuccess?.(mobileRouteID);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(suspendRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* resumeRouteSaga({ payload: mobileRouteID, meta }) {
  try {
    yield axios.post(`${MOBILE_ROUTES}/${mobileRouteID}/resume`);
    yield put(resumeRouteSuccess({ mobileRouteID }));
    meta?.onSuccess?.(mobileRouteID);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(resumeRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* deleteRouteSaga({ payload: mobileRouteID, meta }) {
  try {
    const { payload } = yield axios.delete(`${MOBILE_ROUTES}/${mobileRouteID}`);
    yield put(deleteRouteSuccess({ mobileRouteID, data: payload }));
    meta?.onSuccess?.(payload);
  } catch (err) {
    const failurePayload = failurePayloadFrom(err);
    yield put(deleteRouteFailure(failurePayload));
    meta?.onFailure?.(failurePayload);
  } finally {
    meta?.onFinally?.();
  }
}

function* runsSaga({ payload: { mobileRouteID, page, pageSize, sortBy = 'dateStarted', sortDir = SORT_DIRECTION.DESC } }) {
  try {
    const { payload } = yield axios.get(`${MOBILE_ROUTES}/${mobileRouteID}/runs`, {
      params: { page, pageSize, sortBy, sortDir },
    });
    yield put(runsSuccess({ page, pageSize, ...payload, mobileRouteID }));
  } catch (err) {
    yield put(runsFailure(failurePayloadFrom(err)));
  }
}

function* runDetailSaga({ payload: { mobileRouteID, mobileRouteRunID } }) {
  try {
    const { payload } = yield axios.get(
      `${MOBILE_ROUTES}/${mobileRouteID}/runs/${mobileRouteRunID}`
    );
    yield put(runDetailSuccess(payload));
  } catch (err) {
    yield put(runDetailFailure(failurePayloadFrom(err)));
  }
}

function* runsListSaga({ payload }) {
  const {
    page = 1,
    pageSize,
    mobilePatrolID,
    mobileRouteID,
    dateFrom,
    dateTo,
    status,
    sortBy = 'dateStarted',
    sortDir = SORT_DIRECTION.DESC,
  } = payload || {};
  const params = { page, pageSize, mobilePatrolID, mobileRouteID, dateFrom, dateTo, status, sortBy, sortDir };
  Object.keys(params).forEach((key) => {
    if (params[key] == null || params[key] === '') delete params[key];
  });
  try {
    const { payload: data } = yield axios.get(`${MOBILE_ROUTES}/runs`, { params });
    yield put(runsListSuccess({ page, pageSize, ...data }));
  } catch (err) {
    yield put(runsListFailure(failurePayloadFrom(err)));
  }
}

function* runByIdSaga({ payload: mobileRouteRunID }) {
  try {
    const { payload } = yield axios.get(`${MOBILE_ROUTES}/runs/${mobileRouteRunID}`);
    yield put(runByIdSuccess(payload));
  } catch (err) {
    yield put(runByIdFailure(failurePayloadFrom(err)));
  }
}

export default function* () {
  yield all([
    yield takeLatest(routesRequest, routesSaga),
    yield takeLatest(detailRequest, detailSaga),
    yield takeLatest(createRouteRequest, createRouteSaga),
    yield takeLatest(updateRouteRequest, updateRouteSaga),
    yield takeLatest(copyRouteRequest, copyRouteSaga),
    yield takeLatest(suspendRouteRequest, suspendRouteSaga),
    yield takeLatest(resumeRouteRequest, resumeRouteSaga),
    yield takeLatest(deleteRouteRequest, deleteRouteSaga),
    yield takeEvery(runsRequest, runsSaga),
    yield takeLatest(runDetailRequest, runDetailSaga),
    yield takeLatest(runsListRequest, runsListSaga),
    yield takeLatest(runByIdRequest, runByIdSaga),
  ]);
}
